/**
 * Shared attendance UI helpers: formatting, status badges and the GPS reader.
 * Status colours follow DESIGN.md: green/amber/red/sky only for status, always
 * with a text label (never colour alone).
 */
import { useEffect, useState } from 'react';
import { Badge } from '../../../components/Badge';
import type { AdjustmentType, DayStatus, LocationStatus, SessionStatus } from '../../../store/attendanceStore';

type Tone = 'success' | 'warning' | 'destructive' | 'info' | 'primary' | 'muted';

export const fmtTime = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—';

export const fmtDate = (date: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }) =>
    new Date(`${date}T00:00:00`).toLocaleDateString(undefined, opts);

export const fmtDateTime = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** 485 → "8h 05m". */
export const fmtMinutes = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined) return '—';
    const m = Math.max(0, Math.round(minutes));
    return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};

/** Minutes elapsed since an ISO time, updating every 30 s. */
export function useElapsedMinutes(since: string | null | undefined) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!since) return undefined;
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [since]);
    return since ? Math.max(0, Math.round((now - new Date(since).getTime()) / 60000)) : 0;
}

/** Today in the browser's calendar, YYYY-MM-DD. */
export const localDateString = (d = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Local date + "HH:MM" → ISO timestamp. */
export const toIso = (date: string, time: string) => new Date(`${date}T${time}:00`).toISOString();

// ───────────────────────────── badges ─────────────────────────────

const DAY: Record<DayStatus, { label: string; tone: Tone }> = {
    PRESENT: { label: 'Present', tone: 'success' },
    HALF_DAY: { label: 'Half day', tone: 'warning' },
    ABSENT: { label: 'Absent', tone: 'destructive' },
    IN_PROGRESS: { label: 'In progress', tone: 'info' },
    MISSING_CHECKOUT: { label: 'Missing check-out', tone: 'warning' },
    ON_LEAVE: { label: 'On leave', tone: 'info' },
    HOLIDAY: { label: 'Holiday', tone: 'muted' },
    WEEK_OFF: { label: 'Week off', tone: 'muted' },
    NOT_CHECKED_IN: { label: 'Not checked in', tone: 'muted' },
};
export const dayStatusLabel = (s: DayStatus) => DAY[s]?.label ?? s;
export function DayStatusBadge({ status }: { status: DayStatus | null | undefined }) {
    if (!status) return <Badge variant="muted">No record</Badge>;
    const d = DAY[status] ?? { label: status, tone: 'muted' as Tone };
    return <Badge variant={d.tone}>{d.label}</Badge>;
}

const SESSION: Record<SessionStatus, { label: string; tone: Tone }> = {
    OPEN: { label: 'In progress', tone: 'info' },
    COMPLETED: { label: 'Completed', tone: 'success' },
    MISSING_CHECKOUT: { label: 'Missing check-out', tone: 'warning' },
    SUPERSEDED: { label: 'Replaced by correction', tone: 'muted' },
    ADJUSTED: { label: 'Adjusted', tone: 'primary' },
    CANCELLED: { label: 'Cancelled', tone: 'muted' },
};
export function SessionStatusBadge({ status }: { status: SessionStatus }) {
    const s = SESSION[status] ?? { label: status, tone: 'muted' as Tone };
    return <Badge variant={s.tone}>{s.label}</Badge>;
}

const LOCATION: Record<LocationStatus, { label: string; tone: Tone }> = {
    VALID: { label: 'Location verified', tone: 'success' },
    OUTSIDE_ALLOWED_LOCATION: { label: 'Outside allowed area', tone: 'warning' },
    UNASSIGNED_LOCATION: { label: 'Not your assigned location', tone: 'warning' },
    LOW_ACCURACY: { label: 'Weak GPS signal', tone: 'warning' },
    UNAVAILABLE: { label: 'No location', tone: 'warning' },
    NOT_CHECKED: { label: 'Not checked', tone: 'muted' },
};
/** A verified reading outside any office (work from anywhere) shows as "Home". */
export function LocationStatusBadge({ status, workMode }: { status: LocationStatus | null | undefined; workMode?: string | null }) {
    if (!status) return null;
    if (status === 'VALID' && workMode === 'WFH') return <Badge variant="info">Home</Badge>;
    const l = LOCATION[status] ?? { label: status, tone: 'muted' as Tone };
    return <Badge variant={l.tone}>{l.label}</Badge>;
}

const ADJ: Record<string, { label: string; tone: Tone }> = {
    pending: { label: 'Waiting for review', tone: 'warning' },
    approved: { label: 'Approved', tone: 'success' },
    rejected: { label: 'Rejected', tone: 'destructive' },
    cancelled: { label: 'Cancelled', tone: 'muted' },
};
export function AdjustmentStatusBadge({ status }: { status: string }) {
    const a = ADJ[status] ?? { label: status, tone: 'muted' as Tone };
    return <Badge variant={a.tone}>{a.label}</Badge>;
}

/** Employee-friendly wording for each adjustment type. */
export const ADJUSTMENT_OPTIONS: { type: AdjustmentType; label: string; help: string }[] = [
    { type: 'MISSED_CHECK_OUT', label: 'I forgot to check out', help: 'Pick the session and enter the time you actually left.' },
    { type: 'MISSED_BOTH', label: 'I forgot to check in and out', help: 'Enter when you started and when you finished.' },
    { type: 'MISSED_CHECK_IN', label: 'I forgot to check in', help: 'Enter when you started and when you finished.' },
    { type: 'TIME_CORRECTION', label: 'My check-in or check-out time is wrong', help: 'Pick the session and enter the correct time(s).' },
    { type: 'LOCATION_CORRECTION', label: 'My location is wrong', help: 'Pick the session and the place you actually worked from.' },
    { type: 'OTHER', label: 'Something else', help: 'Explain what happened; HR will review and correct it.' },
];
export const adjustmentLabel = (t: string) =>
    ADJUSTMENT_OPTIONS.find((o) => o.type === t)?.label
    ?? ({ WORK_DURATION_CORRECTION: 'Work duration correction', SESSION_CORRECTION: 'Session correction' } as Record<string, string>)[t] ?? t;

// ───────────────────────────── GPS ─────────────────────────────

export interface GpsResult { latitude: number; longitude: number; accuracy: number }

/** Reads the device location once. Rejects with a message the employee can act on. */
export function readGps(): Promise<GpsResult> {
    return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
            reject(new Error('This browser cannot share your location.'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            (err) => reject(new Error(
                err.code === 1 ? 'Location access is blocked for this site. Allow it in your browser settings.'
                    : err.code === 3 ? 'Getting your location took too long.'
                        : 'Your location is not available right now.')),
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
    });
}
